import React, { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom'
import { MessageCircle, X, LayoutDashboard, FileText, Calculator, ShoppingBag, FileCheck, User, LogOut, ChevronDown, Building2, Plus, Sparkles, Users, Sun, Moon, Monitor, Bell, CheckCircle2, AlertCircle, Layers, Shield, Edit3, Save, RotateCcw, Unlock, Lock } from 'lucide-react'
import { ChatInterface } from '../chat/ChatInterface'
import { useAuth } from '../../contexts/AuthContext'
import { useExchange } from '../../contexts/ExchangeContext'
import { useTheme } from '../../contexts/ThemeContext'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { exchangeApi } from '../../services/api/exchange'
import { authApi } from '../../services/api/auth'
import { demoReset } from '../../services/demoReset'
import { activityLog } from '../../services/activityLog'
import toast from 'react-hot-toast'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../ui/DropdownMenu'
import { ExchangeWizardHub } from '../wizard/ExchangeWizardHub'
import { Logo } from '../ui/Logo'

interface NavItemWithTooltipProps {
  item: { path: string; label: string; icon: any }
  Icon: any
  isActive: boolean
}

const NavItemWithTooltip: React.FC<NavItemWithTooltipProps> = ({ item, Icon, isActive }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  
  return (
    <Link
      to={item.path}
      className={`group relative flex items-center gap-1.5 md:gap-2.5 px-3 md:px-4 lg:px-5 py-2 md:py-3 rounded-2xl transition-all duration-300 outline-none focus:outline-none whitespace-nowrap ${
        isActive
          ? 'text-primary-600 dark:text-primary-400 font-bold underline decoration-2 underline-offset-4'
          : 'text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium'
      }`}
      style={{ flexShrink: 0, minWidth: 'fit-content' }}
      title={item.label}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onTouchStart={() => setShowTooltip(true)}
      onTouchEnd={() => {
        setTimeout(() => setShowTooltip(false), 2000)
      }}
    >
      {/* Hover Background - only for non-active items */}
      {!isActive && (
        <div className="absolute inset-0 bg-white/40 dark:bg-slate-700/40  rounded-2xl opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300"></div>
      )}
      
      {/* Content */}
      <Icon className={`h-5 w-5 relative z-10 flex-shrink-0 transition-transform duration-200 group-hover:scale-110 group-active:scale-95 ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}`} />
      <span className={`text-sm relative z-10 hidden lg:inline ${isActive ? 'text-primary-600 dark:text-primary-400 font-bold' : ''}`}>{item.label}</span>
      
      {/* Tooltip for mobile - shows on hover and touch */}
      <div className={`lg:hidden absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900 dark:bg-slate-700 text-white text-xs font-medium rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap z-50 ${
        showTooltip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
      }`}>
        {item.label}
        {/* Arrow */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
          <div className="w-2 h-2 bg-slate-900 dark:bg-slate-700 rotate-45"></div>
        </div>
      </div>
    </Link>
  )
}

interface InteractiveButtonProps {
  onClick: () => void
  className?: string
  tooltip: string
  children: React.ReactNode
}

const InteractiveButton: React.FC<InteractiveButtonProps> = ({ onClick, className, tooltip, children }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  
  return (
    <button
      onClick={onClick}
      className={className}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onTouchStart={() => setShowTooltip(true)}
      onTouchEnd={() => {
        setTimeout(() => setShowTooltip(false), 2000)
      }}
    >
      {children}
      {/* Tooltip for mobile - shows on hover and touch */}
      <div className={`lg:hidden absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900 dark:bg-slate-700 text-white text-xs font-medium rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap z-50 ${
        showTooltip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
      }`}>
        {tooltip}
        {/* Arrow */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
          <div className="w-2 h-2 bg-slate-900 dark:bg-slate-700 rotate-45"></div>
        </div>
      </div>
    </button>
  )
}

export const Layout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, logout, user } = useAuth()
  const { exchanges, currentExchange, setCurrentExchange, createExchange, refreshExchanges, isLoading: exchangesLoading } = useExchange()
  const { theme, setTheme } = useTheme()
  const [showMobileChat, setShowMobileChat] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [activityMenuOpen, setActivityMenuOpen] = useState(false)
  
  // Check if user is a demo user (demo@open1031.ai or demo1@open1031.ai)
  const isDemoUser = useMemo(() => {
    if (!user?.email) return false
    const userEmail = String(user.email).toLowerCase().trim()
    return userEmail === 'demo@open1031.ai' || userEmail === 'demo1@open1031.ai'
  }, [user?.email])
  
  // Check if hubs are currently unlocked (reactive state)
  const [areHubsUnlocked, setAreHubsUnlocked] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('demo_unlock_all') === 'true'
    }
    return false
  })

  // Update unlock state when it changes
  useEffect(() => {
    const checkUnlockState = () => {
      setAreHubsUnlocked(localStorage.getItem('demo_unlock_all') === 'true')
    }
    checkUnlockState()
    window.addEventListener('storage', checkUnlockState)
    return () => window.removeEventListener('storage', checkUnlockState)
  }, [])
  const [createExchangeOpen, setCreateExchangeOpen] = useState(false)
  const [hubOpen, setHubOpen] = useState(false)
  const [isCreatingExchange, setIsCreatingExchange] = useState(false)
  const [editingExchange, setEditingExchange] = useState<{ id: string; name: string } | null>(null)
  const [editExchangeName, setEditExchangeName] = useState('')
  const [isUpdatingExchange, setIsUpdatingExchange] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)
  const activityRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLElement>(null)

  const handleCreateExchange = async (method: 'neela' | 'hub') => {
    if (method === 'hub') {
      setCreateExchangeOpen(false)
      setHubOpen(true)
      return
    }

    // Create via Neela (AI assistant)
    setIsCreatingExchange(true)
    try {
      const exchangeName = `Exchange ${new Date().toLocaleDateString()}`
      await createExchange(exchangeName)
      setCreateExchangeOpen(false)
    } catch (error) {
      console.error('Failed to create exchange:', error)
    } finally {
      setIsCreatingExchange(false)
    }
  }

  const handleStartProcess = () => {
    // Account creation happens through chat
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const handleEditExchange = (exchange: { id: string; name: string }) => {
    setEditingExchange(exchange)
    setEditExchangeName(exchange.name)
  }

  const handleUpdateExchange = async () => {
    if (!editingExchange || !editExchangeName.trim()) return

    setIsUpdatingExchange(true)
    try {
      await exchangeApi.updateExchange(editingExchange.id, { name: editExchangeName.trim() })
      await refreshExchanges()
      if (currentExchange?.id === editingExchange.id) {
        setCurrentExchange({ ...currentExchange, name: editExchangeName.trim() })
      }
      toast.success('Exchange name updated successfully')
      setEditingExchange(null)
      setEditExchangeName('')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update exchange name')
    } finally {
      setIsUpdatingExchange(false)
    }
  }

  const handleResetDemo = async () => {
    if (isDemoUser) {
      if (!confirm('Are you sure you want to reset the demo data? This will clear all exchange progress, documents, and hub submissions. You will need to start the process from the beginning.')) {
        return
      }
      
      try {
        const authToken = localStorage.getItem('auth_token')
        const currentExchangeId = localStorage.getItem('current_exchange_id')
        
        try {
          await authApi.resetDemo()
        } catch (error) {
          console.warn('Backend reset failed, continuing with client-side reset:', error)
        }
        
        demoReset.resetAll()
        activityLog.clearActivities()
        localStorage.removeItem('property_sale_date')
        
        if (user?.id) {
          import('../../lib/demoInit').then(({ initializeDemoUser }) => {
            initializeDemoUser(String(user.id))
          })
        }
        
        if (user?.id) {
          localStorage.removeItem(`kyc_modal_shown_${user.id}`)
        }
        
        if (authToken) {
          localStorage.setItem('auth_token', authToken)
        }
        if (currentExchangeId) {
          localStorage.setItem('current_exchange_id', currentExchangeId)
        }
        
        toast.success('Demo data has been reset')
        window.location.reload()
      } catch (error) {
        console.error('Reset demo failed:', error)
        toast.error('Failed to reset demo data')
      }
    }
  }

  const handleUnlockAllHubs = () => {
    if (isDemoUser) {
      const now = new Date().toISOString()
      const userName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'
      
      localStorage.setItem('exchange_agreement_signed', 'true')
      localStorage.setItem('exchange_agreement_signature', 'Demo Signature')
      localStorage.setItem('exchange_agreement_signed_date', now)
      localStorage.setItem('exchange_agreement_signed_by', userName)
      
      localStorage.setItem('w9_signed', 'true')
      localStorage.setItem('w9_signature', 'Demo Signature')
      localStorage.setItem('w9_signed_date', now)
      localStorage.setItem('w9_signed_by', userName)
      
      localStorage.setItem('sales_hub_submitted', 'true')
      localStorage.setItem('sales_hub_submitted_date', now)
      
      localStorage.setItem('identification_hub_submitted', 'true')
      localStorage.setItem('identification_hub_submitted_date', now)
      
      localStorage.setItem('purchase_hub_submitted', 'true')
      localStorage.setItem('purchase_hub_submitted_date', now)
      
      localStorage.setItem('acquisition_hub_submitted', 'true')
      localStorage.setItem('acquisition_hub_submitted_date', now)
      
      localStorage.setItem('disbursement_hub_submitted', 'true')
      localStorage.setItem('disbursement_hub_submitted_date', now)
      
      localStorage.setItem('demo_unlock_all', 'true')
      
      if (user && !user.isIdentityVerified) {
        localStorage.setItem('demo_identity_verified', 'true')
      }
      
      setAreHubsUnlocked(true)
      toast.success('All hubs unlocked for demo')
      window.location.reload()
    }
  }

  const handleLockAllHubs = () => {
    if (isDemoUser) {
      localStorage.removeItem('exchange_agreement_signed')
      localStorage.removeItem('exchange_agreement_signature')
      localStorage.removeItem('exchange_agreement_signed_date')
      localStorage.removeItem('exchange_agreement_signed_by')
      localStorage.removeItem('exchange_agreement_data')
      
      localStorage.removeItem('w9_signed')
      localStorage.removeItem('w9_signature')
      localStorage.removeItem('w9_signed_date')
      localStorage.removeItem('w9_signed_by')
      
      localStorage.removeItem('sales_hub_submitted')
      localStorage.removeItem('sales_hub_submitted_date')
      
      localStorage.removeItem('identification_hub_submitted')
      localStorage.removeItem('identification_hub_submitted_date')
      
      localStorage.removeItem('purchase_hub_submitted')
      localStorage.removeItem('purchase_hub_submitted_date')
      
      localStorage.removeItem('acquisition_hub_submitted')
      localStorage.removeItem('acquisition_hub_submitted_date')
      
      localStorage.removeItem('disbursement_hub_submitted')
      localStorage.removeItem('disbursement_hub_submitted_date')
      
      localStorage.removeItem('demo_unlock_all')
      localStorage.removeItem('demo_identity_verified')
      
      setAreHubsUnlocked(false)
      toast.success('All hubs locked for demo')
      window.location.reload()
    }
  }

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false)
      }
      if (activityRef.current && !activityRef.current.contains(event.target as Node)) {
        setActivityMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const headerElement = headerRef.current
    const footerElement = footerRef.current
    if (!headerElement || !footerElement) return
    const updateOffsets = () => {
      const headerHeight = headerElement.getBoundingClientRect().height
      const footerHeight = footerElement.getBoundingClientRect().height
      document.documentElement.style.setProperty('--app-header-offset', `${headerHeight}px`)
      document.documentElement.style.setProperty('--app-footer-offset', `${footerHeight}px`)
    }
    updateOffsets()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateOffsets)
      return () => {
        window.removeEventListener('resize', updateOffsets)
      }
    }
    const observer = new ResizeObserver(updateOffsets)
    observer.observe(headerElement)
    observer.observe(footerElement)
    window.addEventListener('resize', updateOffsets)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateOffsets)
    }
  }, [])

  // Force re-render when wizard data updates
  const [activityRefreshTrigger, setActivityRefreshTrigger] = React.useState(0)
  React.useEffect(() => {
    const handleUpdate = () => {
      setActivityRefreshTrigger(prev => prev + 1)
    }
    window.addEventListener('wizardDataUpdated', handleUpdate)
    window.addEventListener('salesHubCompleted', handleUpdate)
    window.addEventListener('identificationHubCompleted', handleUpdate)
    window.addEventListener('purchaseHubCompleted', handleUpdate)
    window.addEventListener('disbursementHubCompleted', handleUpdate)
    return () => {
      window.removeEventListener('wizardDataUpdated', handleUpdate)
      window.removeEventListener('salesHubCompleted', handleUpdate)
      window.removeEventListener('identificationHubCompleted', handleUpdate)
      window.removeEventListener('purchaseHubCompleted', handleUpdate)
      window.removeEventListener('disbursementHubCompleted', handleUpdate)
    }
  }, [])

  // Dynamic activities based on sequential demo flow (demo1 user only)
  const allActivities = useMemo(() => {
    if (!user?.email || String(user.email).toLowerCase().trim() !== 'demo1@open1031.ai') {
      return []
    }
    const isIdentityVerified = user.isIdentityVerified || localStorage.getItem('demo_identity_verified') === 'true'
    const salesHubSubmitted = localStorage.getItem('sales_hub_submitted') === 'true'
    const identificationHubSubmitted = localStorage.getItem('identification_hub_submitted') === 'true'
    const purchaseHubSubmitted = localStorage.getItem('purchase_hub_submitted') === 'true'
    const disbursementHubSubmitted = localStorage.getItem('disbursement_hub_submitted') === 'true'
    
    const tasks = [
      {
        id: 1,
        type: isIdentityVerified ? 'completed' : 'incomplete',
        title: 'Identity Verification',
        description: 'Complete your identity verification for faster processing',
        action: () => {
          setActivityMenuOpen(false)
          navigate('/app')
        },
        urgent: !isIdentityVerified,
        priority: 1,
        completed: isIdentityVerified
      },
      {
        id: 2,
        type: salesHubSubmitted ? 'completed' : (isIdentityVerified ? 'incomplete' : 'pending'),
        title: 'Complete Sales Hub',
        description: 'Finish entering your relinquished property details',
        action: () => {
          if (isIdentityVerified) {
            setActivityMenuOpen(false)
            navigate('/app')
          }
        },
        urgent: isIdentityVerified && !salesHubSubmitted,
        priority: 2,
        completed: salesHubSubmitted,
        locked: !isIdentityVerified
      },
      {
        id: 3,
        type: identificationHubSubmitted ? 'completed' : (salesHubSubmitted ? 'incomplete' : 'pending'),
        title: 'Complete Identification Hub',
        description: 'Identify replacement properties within 45 days',
        action: () => {
          if (salesHubSubmitted) {
            setActivityMenuOpen(false)
            navigate('/app')
          }
        },
        urgent: salesHubSubmitted && !identificationHubSubmitted,
        priority: 3,
        completed: identificationHubSubmitted,
        locked: !salesHubSubmitted
      },
      {
        id: 4,
        type: purchaseHubSubmitted ? 'completed' : (identificationHubSubmitted ? 'incomplete' : 'pending'),
        title: 'Complete Purchase Hub',
        description: 'Enter replacement property and closing details',
        action: () => {
          if (identificationHubSubmitted) {
            setActivityMenuOpen(false)
            navigate('/app')
          }
        },
        urgent: identificationHubSubmitted && !purchaseHubSubmitted,
        priority: 4,
        completed: purchaseHubSubmitted,
        locked: !identificationHubSubmitted
      },
      {
        id: 5,
        type: disbursementHubSubmitted ? 'completed' : (purchaseHubSubmitted ? 'incomplete' : 'pending'),
        title: 'Complete Disbursement Hub',
        description: 'Provide wire and disbursement instructions',
        action: () => {
          if (purchaseHubSubmitted) {
            setActivityMenuOpen(false)
            navigate('/app')
          }
        },
        urgent: purchaseHubSubmitted && !disbursementHubSubmitted,
        priority: 5,
        completed: disbursementHubSubmitted,
        locked: !purchaseHubSubmitted
      }
    ]
    
    return tasks
  }, [user, navigate, activityRefreshTrigger])

  // Sort activities: urgent first, then by priority
  const activities = allActivities.sort((a, b) => {
    if (a.urgent && !b.urgent) return -1
    if (!a.urgent && b.urgent) return 1
    return a.priority - b.priority
  })
  
  const urgentCount = activities.filter(a => a.urgent && !a.completed && !a.locked).length
  const totalCount = activities.length
  const nextTask = activities.find(a => !a.completed && !a.locked) || null

  // Get user initials for avatar
  const getInitials = () => {
    if (!user) return '??'
    const firstInitial = user.firstName?.[0] || ''
    const lastInitial = user.lastName?.[0] || ''
    const initials = (firstInitial + lastInitial).toUpperCase()
    return initials || user.email?.[0]?.toUpperCase() || '??'
  }

  // Render avatar (image or initials)
  const renderAvatar = (size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'w-10 h-10 text-sm',
      md: 'w-11 h-11 text-sm',
      lg: 'w-12 h-12 text-base'
    }
    
    if (user?.avatar) {
      return (
        <img 
          src={user.avatar} 
          alt={`${user.firstName} ${user.lastName}`}
          className={`${sizeClasses[size]} rounded-full object-cover ring-4 ring-white/50 shadow-xl`}
        />
      )
    }
    
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center text-white font-bold shadow-xl ring-4 ring-white/50`}>
        {getInitials()}
      </div>
    )
  }

  const navItems = [
    { path: '/app', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/app/documents', label: 'Documents', icon: FileText },
    { path: '/app/calculator', label: 'Calculator', icon: Calculator },
    { path: '/app/marketplace', label: 'Marketplace', icon: ShoppingBag },
    { path: '/app/forms', label: 'Forms', icon: FileCheck },
    { path: '/app/users', label: 'Users', icon: Users },
    ...(user?.isAdmin ? [{ path: '/app/admin', label: 'Admin', icon: Shield }] : [])
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden flex flex-col transition-colors duration-300">

      {/* Floating Top Navigation Bar */}
      <div ref={headerRef} className="px-4 sm:px-6 lg:px-8 pt-8 sticky top-0 z-50 flex-shrink-0">
        <nav className="max-w-7xl mx-auto bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/50 dark:border-slate-700/50 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-visible">
          <div className="px-4 md:px-6 lg:px-8 overflow-visible rounded-[28px]">
            <div className="flex items-center h-20 gap-3 md:gap-4 lg:gap-5 overflow-visible">
              {/* Logo */}
              <Link to="/app" className="flex items-center space-x-2 flex-shrink-0 group">
                <Logo className="h-10 w-auto transition-transform group-hover:scale-105" />
              </Link>


              {/* Navigation - Mobile (icons only with tooltips) and Desktop */}
              <div className="flex items-center gap-2 md:gap-3 lg:gap-4 flex-1 justify-center px-2 md:px-3 lg:px-4 min-w-0 overflow-x-auto overflow-y-hidden scrollbar-hide">
                <div className="flex items-center gap-2 md:gap-3 lg:gap-4 justify-center lg:justify-start" style={{ flexWrap: 'nowrap' }}>
                  {navItems.map((item) => {
                    const Icon = item.icon
                    const isActive = location.pathname === item.path || (item.path === '/app/admin' && location.pathname.startsWith('/app/admin'))
                    return (
                      <NavItemWithTooltip
                        key={item.path}
                        item={item}
                        Icon={Icon}
                        isActive={isActive}
                      />
                    )
                  })}
                </div>
              </div>

              {/* Right side - Notifications + Profile */}
              <div className="flex items-center gap-2 flex-shrink-0 ml-2 lg:ml-4" style={{ minWidth: 'fit-content', position: 'relative', zIndex: 10000 }}>
                {/* Activity/Notifications Bell */}
                <div className="relative" ref={activityRef} style={{ zIndex: 10001 }}>
                  <button
                    onClick={() => setActivityMenuOpen(!activityMenuOpen)}
                    className={`group relative flex items-center justify-center p-2 rounded-2xl transition-colors duration-300 hover:bg-slate-50 dark:hover:bg-slate-700/40 ${
                      totalCount > 0 ? 'animate-bounce-up-down' : ''
                    }`}
                    type="button"
                  >
                    <Bell className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                    {totalCount > 0 && (
                      <span className={`absolute -top-1 -right-1 h-5 w-5 text-white text-xs font-bold rounded-full flex items-center justify-center ring-2 ring-white dark:ring-slate-800 ${
                        urgentCount > 0 
                          ? 'bg-red-500 animate-pulse' 
                          : 'bg-[#69adba] dark:bg-[#69adba]'
                      }`}>
                        {totalCount}
                      </span>
                    )}
                  </button>

                  {/* Activity Dropdown */}
                  {activityMenuOpen && (
                    <div className="absolute right-0 top-full mt-3 w-80 z-[10000] animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-hidden">
                        <div className="px-5 py-4 bg-gradient-to-br from-slate-50 dark:from-slate-700/50 to-transparent border-b border-white/50 dark:border-slate-700/50">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                              <Bell className="h-4 w-4 text-[#69adba] dark:text-[#69adba]" />
                              Tasks & Notifications
                            </h3>
                            {activities.length > 0 && (
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                urgentCount > 0 
                                  ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                  : 'bg-[#69adba]/10 dark:bg-[#69adba]/20 text-[#69adba] dark:text-[#69adba]'
                              }`}>
                                {activities.length} item{activities.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          {nextTask && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-slate-600 dark:text-slate-400">Next:</span>
                              <span className="font-medium text-slate-900 dark:text-slate-100 truncate">
                                {nextTask.title}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                          {activities.length > 0 ? (
                            <div className="p-2">
                              {activities.map((activity, index) => {
                                const isCompleted = activity.completed
                                const isLocked = activity.locked
                                const isCurrent = activity.urgent && !isCompleted && !isLocked
                                
                                return (
                                  <button
                                    key={activity.id}
                                    onClick={() => {
                                      if (!isLocked && !isCompleted) {
                                        setActivityMenuOpen(false)
                                        activity.action()
                                      }
                                    }}
                                    disabled={isLocked}
                                    className={`w-full text-left p-3 rounded-2xl transition-all duration-200 group relative ${
                                      isLocked 
                                        ? 'opacity-50 cursor-not-allowed' 
                                        : isCompleted
                                        ? 'opacity-70'
                                        : 'hover:bg-white/60 dark:hover:bg-slate-700/60'
                                    } ${
                                      isCurrent ? 'ring-2 ring-[#69adba] dark:ring-[#69adba] ring-offset-2 dark:ring-offset-slate-800' : ''
                                    }`}
                                    type="button"
                                  >
                                    {isCurrent && (
                                      <div className="absolute -left-1 -top-1 bg-[#69adba] dark:bg-[#69adba] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                        NEXT
                                      </div>
                                    )}
                                    <div className="flex items-start gap-3">
                                      <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center ${
                                        isCompleted
                                          ? 'bg-green-100 dark:bg-green-900/30'
                                          : isCurrent
                                          ? 'bg-red-100 dark:bg-red-900/30'
                                          : 'bg-slate-100 dark:bg-slate-700/30'
                                      }`}>
                                        {isCompleted ? (
                                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                                        ) : isCurrent ? (
                                          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                        ) : (
                                          <Lock className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                          <p className={`text-sm font-semibold ${
                                            isCompleted 
                                              ? 'text-slate-600 dark:text-slate-400 line-through'
                                              : 'text-slate-900 dark:text-slate-100'
                                          }`}>
                                            {activity.title}
                                          </p>
                                          {isCurrent && (
                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                                              URGENT
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-slate-600 dark:text-slate-400">
                                          {activity.description}
                                        </p>
                                      </div>
                                    </div>
                                  </button>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="p-8 text-center">
                              <CheckCircle2 className="h-12 w-12 text-green-500 dark:text-green-400 mx-auto mb-3" />
                              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">All caught up!</p>
                              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                No pending tasks or notifications
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Profile Menu */}
                <div className="relative" ref={profileRef} style={{ zIndex: 10001 }}>
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="group relative flex items-center px-2 lg:px-3 py-2 rounded-2xl transition-all duration-300 hover:bg-white/40 dark:hover:bg-slate-700/40 hover:"
                  >
                    {/* Avatar Circle */}
                    <div className="w-9 h-9 lg:w-11 lg:h-11 rounded-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 dark:from-slate-600 dark:via-slate-700 dark:to-slate-800 flex items-center justify-center text-white font-bold text-xs lg:text-sm shadow-xl ring-2 lg:ring-4 ring-white/50 dark:ring-slate-700/50 group-hover:ring-white/70 dark:group-hover:ring-slate-600/70 group-hover:shadow-2xl transition-all duration-300">
                      {getInitials()}
                    </div>
                  </button>

                  {/* Profile Dropdown Menu */}
                  {profileMenuOpen && (
                    <div className="absolute right-0 top-full mt-3 w-64 z-[10000] animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-hidden">
                        {/* User Info */}
                        <div className="px-5 py-4 bg-gradient-to-br from-slate-50 dark:from-slate-700/50 to-transparent border-b border-white/50 dark:border-slate-700/50">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 dark:from-slate-600 dark:via-slate-700 dark:to-slate-800 flex items-center justify-center text-white font-bold shadow-xl">
                              {getInitials()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate flex items-center gap-2">
                                <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                                {user?.firstName} {user?.lastName}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {user?.email}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Menu Items */}
                        <div className="p-2">
                          <Link
                            to="/app/profile"
                            onClick={() => setProfileMenuOpen(false)}
                            className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/60 hover: transition-all duration-200"
                          >
                            <div className="w-8 h-8 rounded-xl bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center group-hover:bg-primary-200 dark:group-hover:bg-primary-800/50 transition-colors">
                              <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                            </div>
                            <span className="font-medium">Profile Settings</span>
                          </Link>

                          {/* Theme Toggle */}
                          <div className="px-4 py-2 mt-2">
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Theme</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setTheme('light')}
                                className={`flex-1 flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                                  theme === 'light'
                                    ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 ring-2 ring-primary-500/50'
                                    : 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                              >
                                <Sun className="h-4 w-4" />
                                <span className="text-xs font-medium">Light</span>
                              </button>
                              <button
                                onClick={() => setTheme('dark')}
                                className={`flex-1 flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                                  theme === 'dark'
                                    ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 ring-2 ring-primary-500/50'
                                    : 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                              >
                                <Moon className="h-4 w-4" />
                                <span className="text-xs font-medium">Dark</span>
                              </button>
                              <button
                                onClick={() => setTheme('system')}
                                className={`flex-1 flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                                  theme === 'system'
                                    ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 ring-2 ring-primary-500/50'
                                    : 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                              >
                                <Monitor className="h-4 w-4" />
                                <span className="text-xs font-medium">Auto</span>
                              </button>
                            </div>
                          </div>

                          {isDemoUser && (
                            <>
                              <div className="border-t border-slate-200 dark:border-slate-700 my-2"></div>
                              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 px-4 py-2">Demo Actions</p>
                              {areHubsUnlocked ? (
                                <button
                                  onClick={() => {
                                    setProfileMenuOpen(false)
                                    handleLockAllHubs()
                                  }}
                                  className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/60 transition-all duration-200 w-full"
                                >
                                  <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center group-hover:bg-slate-200 dark:group-hover:bg-slate-600/50 transition-colors">
                                    <Lock className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                                  </div>
                                  <span className="font-medium">Lock All Hubs</span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    setProfileMenuOpen(false)
                                    handleUnlockAllHubs()
                                  }}
                                  className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/60 transition-all duration-200 w-full"
                                >
                                  <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center group-hover:bg-slate-200 dark:group-hover:bg-slate-600/50 transition-colors">
                                    <Unlock className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                                  </div>
                                  <span className="font-medium">Unlock All Hubs</span>
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setProfileMenuOpen(false)
                                  handleResetDemo()
                                }}
                                className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-all duration-200 w-full"
                              >
                                <div className="w-8 h-8 rounded-xl bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center group-hover:bg-orange-200 dark:group-hover:bg-orange-800/50 transition-colors">
                                  <RotateCcw className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                                </div>
                                <span className="font-medium">Reset Demo Data</span>
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => {
                              setProfileMenuOpen(false)
                              handleLogout()
                            }}
                            className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all duration-200 w-full mt-2"
                          >
                            <div className="w-8 h-8 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center group-hover:bg-red-200 dark:group-hover:bg-red-800/50 transition-colors">
                              <LogOut className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </div>
                            <span className="font-medium">Logout</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content - No wrapping box for floating cards */}
      <div className="px-4 sm:px-6 lg:px-8 py-8 relative z-10 flex-grow">
        <main className="max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Footer - Glass design matching header */}
      <footer ref={footerRef} className="px-4 sm:px-6 lg:px-8 pb-8 relative z-0 mb-4 mt-auto flex-shrink-0">
        <div className="max-w-7xl mx-auto bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/50 dark:border-slate-700/50 ring-1 ring-black/[0.03] dark:ring-white/[0.03] px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <Logo className="h-10 w-auto" />
              <div className="text-center md:text-left">
                <p className="text-slate-900 dark:text-slate-100 text-sm font-bold">Open1031, Inc.</p>
                <p className="text-slate-600 dark:text-slate-400 text-xs mt-1">
                  Copyright &copy; 2025 All Rights Reserved.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end gap-3 mr-20">
              <div className="flex flex-wrap justify-center md:justify-end gap-6 text-sm text-slate-700 dark:text-slate-300">
                <Link to="/legal#privacy" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium">Privacy Policy</Link>
                <Link to="/legal#terms" className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium">Terms of Service</Link>
              </div>
              <p className="text-slate-600 dark:text-slate-400 text-xs text-center md:text-right">
                The complete 1031 exchange platform for modern investors.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Chat Interface - Desktop (popup) & Mobile (fullscreen) */}
      {showMobileChat && (
        <>
          {/* Mobile - Fullscreen */}
          <div className="fixed inset-0 z-50 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 dark:from-slate-900 dark:via-slate-800/30 dark:to-slate-900/40 lg:hidden">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-4 py-4 bg-white/50 dark:bg-slate-800/50 backdrop-blur-2xl border-b border-white/40 dark:border-slate-700/40">
                <Logo className="h-10 w-auto" />
                <button
                  onClick={() => setShowMobileChat(false)}
                  className="p-2 hover:bg-white/40 dark:hover:bg-slate-700/40 rounded-2xl transition-all"
                >
                  <X className="h-6 w-6 text-slate-600 dark:text-slate-300" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <ChatInterface 
                  onStartProcess={handleStartProcess}
                  className="h-full"
                />
              </div>
            </div>
          </div>

          {/* Desktop - Popup Box */}
          <div className="hidden lg:block fixed bottom-24 right-6 z-50 w-96 h-[600px] min-h-[420px] max-h-[85vh] overflow-hidden resize-y animate-in slide-in-from-bottom-4 fade-in duration-300">
            <div className="flex flex-col h-full bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[28px] shadow-[0_20px_60px_rgba(0,0,0,0.15),0_8px_24px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.2)] border border-white/50 dark:border-slate-700/50 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-br from-slate-50 dark:from-slate-700/50 to-transparent border-b border-white/50 dark:border-slate-700/50">
                <div className="flex items-center space-x-3">
                  <Logo className="h-8 w-auto" />
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Neela</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">AI Assistant</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMobileChat(false)}
                  className="p-2 hover:bg-white/60 dark:hover:bg-slate-700/60 rounded-xl transition-all"
                >
                  <X className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                </button>
              </div>
              
              {/* Chat Content */}
              <div className="flex-1 overflow-hidden">
                <ChatInterface 
                  onStartProcess={handleStartProcess}
                  className="h-full"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Chat Button using Portal - Desktop & Mobile - MUST BE LAST */}
      {isAuthenticated && !showMobileChat && createPortal(
        <>
          {/* Pulsing Ring Animation */}
          <div 
            className="absolute rounded-full bg-gradient-to-br from-[#69adba] to-[#5a9aa6] animate-ping opacity-20"
            style={{
              position: 'fixed',
              bottom: '1.5rem',
              right: '1.5rem',
              width: '4rem',
              height: '4rem',
              zIndex: 2147483646,
              pointerEvents: 'none'
            }}
          />
          
          {/* Main Button */}
          <button
            onClick={() => setShowMobileChat(true)}
            className="relative group h-16 w-16 lg:h-18 lg:w-18 bg-gradient-to-br from-[#69adba] via-[#5fbdce] to-[#69adba] hover:from-[#5fbdce] hover:via-[#69adba] hover:to-[#5fbdce] text-white rounded-full shadow-[0_20px_60px_rgba(105,173,186,0.5),0_8px_24px_rgba(0,0,0,0.2),0_0_0_1px_rgba(255,255,255,0.1)_inset] hover:shadow-[0_24px_72px_rgba(105,173,186,0.6),0_12px_32px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.2)_inset] flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-500 ease-out"
            style={{ 
              position: 'fixed',
              bottom: '1.5rem',
              right: '1.5rem',
              zIndex: 2147483647
            }}
          >
            {/* Glossy Shine Effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/40 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
            
            {/* Icon with subtle animation */}
            <MessageCircle className="h-8 w-8 lg:h-9 lg:w-9 relative z-10 drop-shadow-lg group-hover:rotate-12 transition-transform duration-300" fill="none" style={{ backgroundColor: "transparent" }} />
          </button>
        </>,
        document.body
      )}

      {/* Edit Exchange Modal */}
      {editingExchange && editingExchange.id !== currentExchange?.id && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Edit3 className="h-5 w-5 text-[#69adba]" />
                Edit Exchange Name
              </h2>
              <button
                onClick={() => {
                  setEditingExchange(null)
                  setEditExchangeName('')
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Exchange Name
                </label>
                <Input
                  value={editExchangeName}
                  onChange={(e) => setEditExchangeName(e.target.value)}
                  placeholder="Enter exchange name"
                  className="w-full"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdateExchange()
                    }
                  }}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingExchange(null)
                    setEditExchangeName('')
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateExchange}
                  disabled={!editExchangeName.trim() || isUpdatingExchange}
                  className="flex-1 bg-[#69adba] hover:bg-[#5a9aa6] text-white"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isUpdatingExchange ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Exchange Modal */}
      {createExchangeOpen && !hubOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] p-6 max-w-md w-full mx-4">
            {/* Title with Icon */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-[#69adba] to-[#5a9aa6] flex items-center justify-center flex-shrink-0 shadow-sm">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Create New 1031 Exchange</h2>
              </div>
            </div>
            
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
              Choose how you'd like to create your new exchange:
            </p>
            <div className="space-y-3">
              <Button
                className="w-full justify-start bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#4d8a96] dark:from-[#69adba] dark:to-[#5a9aa6] dark:hover:from-[#5a9aa6] dark:hover:to-[#4d8a96] text-white border-0 shadow-sm"
                onClick={() => handleCreateExchange('neela')}
                disabled={isCreatingExchange}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Via Neela (AI Assistant)
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-slate-400 dark:hover:border-slate-500"
                onClick={() => handleCreateExchange('hub')}
              >
                <Building2 className="mr-2 h-4 w-4 text-slate-600 dark:text-slate-300" />
                Via Exchange Hub
              </Button>
              <Button
                variant="ghost"
                className="w-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100"
                onClick={() => setCreateExchangeOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Exchange Hub Modal */}
      {hubOpen && (
        <ExchangeWizardHub 
          initialTab="initial"
          onClose={() => {
            setHubOpen(false)
            setCreateExchangeOpen(false)
          }}
        />
      )}
    </div>
  )
}
